import type { EnhancedPuzzle, PuzzleData } from '@/types/puzzle';

// Helper function to determine theme based on grid content and level
function getThemeForLevel(grid: string[], levelIndex: number): 'forest' | 'cave' | 'lava' | 'ice' | 'magical' | 'dungeon' {
  const gridString = grid.join('').toLowerCase();
  let theme: 'forest' | 'cave' | 'lava' | 'ice' | 'magical' | 'dungeon' = 'forest';
  if (gridString.includes('l')) theme = 'lava';
  if (gridString.includes('s')) theme = 'ice';
  if (levelIndex > 30) theme = 'magical';
  if (levelIndex > 20) theme = 'cave';
  return theme;
}

export const puzzle1: PuzzleData[] = [
  // Level 1 - Enhanced with configurable collectibles
  {
    grid: ['ox---', '---xe'],
    metadata: {
      level: 0,
      name: 'First Steps',
      difficulty: 'easy',
      theme: 'forest',
      collectibles: [
        { type: 'coin', position: [2, 0], value: 10, id: 'L1_C1' },
      ],
      minStars: 0,
      timeTarget: 20,
      stepTarget: 3
    }
  } as EnhancedPuzzle,

  // Level 2 - Enhanced with gems
  {
    grid: ['ox---', '---xe', '--xxx'],
    metadata: {
      level: 1,
      name: 'Around the Block',
      difficulty: 'easy',
      theme: 'forest',
      collectibles: [
        { type: 'coin', position: [2, 0], value: 10, id: 'L2_C1' },
        { type: 'gem', position: [1, 1], value: 50, id: 'L2_G1' },
        { type: 'coin', position: [0, 2], value: 10, id: 'L2_C2' }
      ],
      minStars: 0,
      timeTarget: 30,
      stepTarget: 5
    }
  } as EnhancedPuzzle,

  // Level 3 - Enhanced with strategic collectibles
  {
    grid: ['o--xx', 'x--xe', '--x--', '-----', '-----'],
    metadata: {
      level: 2,
      name: 'The Long Way',
      difficulty: 'easy',
      theme: 'forest',
      collectibles: [
        { type: 'coin', position: [1, 0], value: 10, id: 'L3_C1' },
        { type: 'coin', position: [2, 1], value: 10, id: 'L3_C2' },
        { type: 'coin', position: [1, 3], value: 10, id: 'L3_C3' },
        { type: 'gem', position: [3, 4], value: 50, id: 'L3_G1' }
      ],
      minStars: 0,
      timeTarget: 45,
      stepTarget: 8
    }
  } as EnhancedPuzzle,

  // --- Legacy Levels Start Here ---

  {
    grid: ['e-xx-', 'x--xx', '--xo-', '-x---', '-----'],
    metadata: {
      level: 3,
      name: 'Legacy Level 1',
      difficulty: 'medium',
      theme: getThemeForLevel(['e-xx-', 'x--xx', '--xo-', '-x---', '-----'], 3),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 10
    }
  } as EnhancedPuzzle,
  {
    grid: ['xx--e', '---xx', '--xxx', '----x', '-x-ox'],
    metadata: {
      level: 4,
      name: 'Legacy Level 2',
      difficulty: 'medium',
      theme: getThemeForLevel(['xx--e', '---xx', '--xxx', '----x', '-x-ox'], 4),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 11
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox--e', '-x-xx', '---xx', '--xxx', 'x-n-x'],
    metadata: {
      level: 5,
      name: 'Legacy Level 3',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox--e', '-x-xx', '---xx', '--xxx', 'x-n-x'], 5),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 12
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox--x', '-x-xe', '---x-', 'x--n-', 'x---x'],
    metadata: {
      level: 6,
      name: 'Legacy Level 4',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox--x', '-x-xe', '---x-', 'x--n-', 'x---x'], 6),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 13
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox---', '---xe', '--xxx', '---x-', 'x--n-'],
    metadata: {
      level: 7,
      name: 'Legacy Level 5',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox---', '---xe', '--xxx', '---x-', 'x--n-'], 7),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 14
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox--x', '----n', 'x--x-', 'x--e-', 'x-n-x'],
    metadata: {
      level: 8,
      name: 'Legacy Level 6',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox--x', '----n', 'x--x-', 'x--e-', 'x-n-x'], 8),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 15
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox---', '--nx-', 'x--n-', '---xx', 'xexxx'],
    metadata: {
      level: 9,
      name: 'Legacy Level 7',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox---', '--nx-', 'x--n-', '---xx', 'xexxx'], 9),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 16
    }
  } as EnhancedPuzzle,
  {
    grid: ['o-x--', '--x-n', 'x-n--', 'x--ex', 'x--xx'],
    metadata: {
      level: 10,
      name: 'Legacy Level 8',
      difficulty: 'medium',
      theme: getThemeForLevel(['o-x--', '--x-n', 'x-n--', 'x--ex', 'x--xx'], 10),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 17
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox--x', '---xn', '--e--', 'x-x-n', 'x-n-x'],
    metadata: {
      level: 11,
      name: 'Legacy Level 9',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox--x', '---xn', '--e--', 'x-x-n', 'x-n-x'], 11),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 18
    }
  } as EnhancedPuzzle,
  {
    grid: ['---x-n-', '-x----e', '--x--n-', 'x---n--', 'o-----x'],
    metadata: {
      level: 12,
      name: 'Legacy Level 10',
      difficulty: 'medium',
      theme: getThemeForLevel(['---x-n-', '-x----e', '--x--n-', 'x---n--', 'o-----x'], 12),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 19
    }
  } as EnhancedPuzzle,
  {
    grid: ['ox---nx---', '----n---xe', '--n--x---x'],
    metadata: {
      level: 13,
      name: 'Legacy Level 11',
      difficulty: 'medium',
      theme: getThemeForLevel(['ox---nx---', '----n---xe', '--n--x---x'], 13),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 20
    }
  } as EnhancedPuzzle,
  {
    grid: ['xxo---x---', '--------x-', '-----x----', '--x-------', '---------e', '-x---x----'],
    metadata: {
      level: 14,
      name: 'Legacy Level 12',
      difficulty: 'medium',
      theme: getThemeForLevel(['xxo---x---', '--------x-', '-----x----', '--x-------', '---------e', '-x---x----'], 14),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 21
    }
  } as EnhancedPuzzle,
  {
    grid: ['xo-lx---', '------xe'],
    metadata: {
      level: 15,
      name: 'Legacy Level 13',
      difficulty: 'medium',
      theme: getThemeForLevel(['xo-lx---', '------xe'], 15),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 22
    }
  } as EnhancedPuzzle,
  {
    grid: ['xx----x--o', '------x---', '--------xx', '--x----x--', '---ll----e', 'xx-xx---x-'],
    metadata: {
      level: 16,
      name: 'Legacy Level 14',
      difficulty: 'medium',
      theme: getThemeForLevel(['xx----x--o', '------x---', '--------xx', '--x----x--', '---ll----e', 'xx-xx---x-'], 16),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 23
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx----x--o',
      '------x---',
      '---x----xx',
      '--x----x--',
      '--x-xx----',
      '---x----xx',
      '--x-------',
      '----xlll--',
      'xx-xxlllxe',
    ],
    metadata: {
      level: 17,
      name: 'Legacy Level 15',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx----x--o',
        '------x---',
        '---x----xx',
        '--x----x--',
        '--x-xx----',
        '---x----xx',
        '--x-------',
        '----xlll--',
        'xx-xxlllxe',
      ], 17),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 24
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx----x--o',
      '------x--x',
      '-lxlx-----',
      '-llx--x-xx',
      '-lx-------',
      '-llx----xx',
      '-lxx---x--',
      '----x-----',
      'xxe-x---x-',
    ],
    metadata: {
      level: 18,
      name: 'Legacy Level 16',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx----x--o',
        '------x--x',
        '-lxlx-----',
        '-llx--x-xx',
        '-lx-------',
        '-llx----xx',
        '-lxx---x--',
        '----x-----',
        'xxe-x---x-',
      ], 18),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 25
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx---nx---',
      '------x--x',
      '--x-x-----',
      'x--x--x-xx',
      'l-xx------',
      'l--xlll--x',
      'l-xxlxlx--',
      'l---xe--x-',
      'xx------xo',
    ],
    metadata: {
      level: 19,
      name: 'Legacy Level 17',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx---nx---',
        '------x--x',
        '--x-x-----',
        'x--x--x-xx',
        'l-xx------',
        'l--xlll--x',
        'l-xxlxlx--',
        'l---xe--x-',
        'xx------xo',
      ], 19),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 26
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx--n-x---',
      '---l--x--x',
      '---l------',
      'x--x--x-x-',
      '-----x--x-',
      '--ex-x----',
      '--x-----x-',
      '----------',
      'xx-------o',
    ],
    metadata: {
      level: 20,
      name: 'Legacy Level 18',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx--n-x---',
        '---l--x--x',
        '---l------',
        'x--x--x-x-',
        '-----x--x-',
        '--ex-x----',
        '--x-----x-',
        '----------',
        'xx-------o',
      ], 20),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 27
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx--n-x---',
      '---l--x--x',
      '---l----l-',
      '------x-x-',
      '-----x--x-',
      '--lx-x----',
      '--x-----x-',
      '-l---xx---',
      'ex-------o',
    ],
    metadata: {
      level: 21,
      name: 'Legacy Level 19',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx--n-x---',
        '---l--x--x',
        '---l----l-',
        '------x-x-',
        '-----x--x-',
        '--lx-x----',
        '--x-----x-',
        '-l---xx---',
        'ex-------o',
      ], 21),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 28
    }
  } as EnhancedPuzzle,
  {
    grid: ['xx--n-xxll', '---x----lx', '-lxx-lx--o', '-x--nlx---', '--elxxx--x'],
    metadata: {
      level: 22,
      name: 'Legacy Level 20',
      difficulty: 'medium',
      theme: getThemeForLevel(['xx--n-xxll', '---x----lx', '-lxx-lx--o', '-x--nlx---', '--elxxx--x'], 22),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 29
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx----xxll',
      'll-x----lx',
      'll-l-xx-lx',
      'll-l-ll-lx',
      '--nx----lx',
      '-lxx-xl--o',
      '-xlllex---',
      '---n--x--x',
    ],
    metadata: {
      level: 23,
      name: 'Legacy Level 21',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx----xxll',
        'll-x----lx',
        'll-l-xx-lx',
        'll-l-ll-lx',
        '--nx----lx',
        '-lxx-xl--o',
        '-xlllex---',
        '---n--x--x',
      ], 23),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 30
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx----llll',
      '------exlx',
      '-l-l-xxxlx',
      '-l-l-ll-xx',
      '---x-n---x',
      '-lxx-xl--o',
      '-xll-lxx--',
      '--n---x--x',
    ],
    metadata: {
      level: 24,
      name: 'Legacy Level 22',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx----llll',
        '------exlx',
        '-l-l-xxxlx',
        '-l-l-ll-xx',
        '---x-n---x',
        '-lxx-xl--o',
        '-xll-lxx--',
        '--n---x--x',
      ], 24),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 31
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xx---n----',
      '--llx--xl-',
      '-x----xlx-',
      '-l---x-xx-',
      '-l-x--n---',
      '---x---x-x',
      '-lxx-xl--o',
      'exll-lxx--',
      'x---n----x',
    ],
    metadata: {
      level: 25,
      name: 'Legacy Level 23',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xx---n----',
        '--llx--xl-',
        '-x----xlx-',
        '-l---x-xx-',
        '-l-x--n---',
        '---x---x-x',
        '-lxx-xl--o',
        'exll-lxx--',
        'x---n----x',
      ], 25),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 32
    }
  } as EnhancedPuzzle,
  {
    grid: ['o---s---', '---n--n-', '---xe--s', '-----n--'],
    metadata: {
      level: 26,
      name: 'Legacy Level 24',
      difficulty: 'medium',
      theme: getThemeForLevel(['o---s---', '---n--n-', '---xe--s', '-----n--'], 26),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 33
    }
  } as EnhancedPuzzle,
  {
    grid: ['o--xxx----', '---xxx-x--', '---n---e--', '-----x-s--', '---xx-n--x', 'x-n----xxx'],
    metadata: {
      level: 27,
      name: 'Legacy Level 25',
      difficulty: 'medium',
      theme: getThemeForLevel(['o--xxx----', '---xxx-x--', '---n---e--', '-----x-s--', '---xx-n--x', 'x-n----xxx'], 27),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 34
    }
  } as EnhancedPuzzle,
  {
    grid: ['o-------s-', '-x-xxx-x--', '---------e', '-----x----', '---xx----x', 'x------xxx'],
    metadata: {
      level: 28,
      name: 'Legacy Level 26',
      difficulty: 'medium',
      theme: getThemeForLevel(['o-------s-', '-x-xxx-x--', '---------e', '-----x----', '---xx----x', 'x------xxx'], 28),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 35
    }
  } as EnhancedPuzzle,
  {
    grid: ['o---------', '---xxx-x--', '-s-s--slse', '-----x----', '---xx----x', 'x-----xxxx'],
    metadata: {
      level: 29,
      name: 'Legacy Level 27',
      difficulty: 'medium',
      theme: getThemeForLevel(['o---------', '---xxx-x--', '-s-s--slse', '-----x----', '---xx----x', 'x-----xxxx'], 29),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 36
    }
  } as EnhancedPuzzle,
  {
    grid: ['o---------', '------s-s-', '-s-s------', '-------ss-', '---s-ss---', '-s-----e--'],
    metadata: {
      level: 30,
      name: 'Legacy Level 28',
      difficulty: 'medium',
      theme: getThemeForLevel(['o---------', '------s-s-', '-s-s------', '-------ss-', '---s-ss---', '-s-----e--'], 30),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 37
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'o---------',
      '------s-s-',
      '-s-s------',
      '--------s-',
      '----s--s--',
      '----s---s-',
      '---s-ss---',
      '-s-----e--',
    ],
    metadata: {
      level: 31,
      name: 'Legacy Level 29',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'o---------',
        '------s-s-',
        '-s-s------',
        '--------s-',
        '----s--s--',
        '----s---s-',
        '---s-ss---',
        '-s-----e--',
      ], 31),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 38
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'o------e--',
      '--lll-s-s-',
      '-s-s------',
      '---llll-s-',
      '----s--s--',
      '--lls---ss',
      '-------ll-',
      '-s--------',
    ],
    metadata: {
      level: 32,
      name: 'Legacy Level 30',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'o------e--',
        '--lll-s-s-',
        '-s-s------',
        '---llll-s-',
        '----s--s--',
        '--lls---ss',
        '-------ll-',
        '-s--------',
      ], 32),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 39
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'o-xxx---x-',
      '--lll-s-s-',
      '-s-s---x--',
      'xx-llll-s-',
      '--n-sxxx--',
      '--lls---ss',
      '---sxxxll-',
      '-s-s---ex-',
    ],
    metadata: {
      level: 33,
      name: 'Legacy Level 31',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'o-xxx---x-',
        '--lll-s-s-',
        '-s-s---x--',
        'xx-llll-s-',
        '--n-sxxx--',
        '--lls---ss',
        '---sxxxll-',
        '-s-s---ex-',
      ], 33),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 40
    }
  } as EnhancedPuzzle,
  {
    grid: [
      '-nxxx---x-',
      '--lll-s-s-',
      '-s-s---xll',
      '-l-llx--s-',
      '---lsxxx--',
      '--lls---ss',
      '---sxxxll-',
      'xs-s---exo',
    ],
    metadata: {
      level: 34,
      name: 'Legacy Level 32',
      difficulty: 'medium',
      theme: getThemeForLevel([
        '-nxxx---x-',
        '--lll-s-s-',
        '-s-s---xll',
        '-l-llx--s-',
        '---lsxxx--',
        '--lls---ss',
        '---sxxxll-',
        'xs-s---exo',
      ], 34),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 41
    }
  } as EnhancedPuzzle,
  {
    grid: [
      '-s--xn--x-',
      'e--ll-s-s-',
      'x--s---xll',
      '-l-l-xlll-',
      '----sxxx--',
      '-l-ls---ss',
      '--s-xxxll-',
      'xs-s-----o',
    ],
    metadata: {
      level: 35,
      name: 'Legacy Level 33',
      difficulty: 'medium',
      theme: getThemeForLevel([
        '-s--xn--x-',
        'e--ll-s-s-',
        'x--s---xll',
        '-l-l-xlll-',
        '----sxxx--',
        '-l-ls---ss',
        '--s-xxxll-',
        'xs-s-----o',
      ], 35),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 42
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'es--l-----',
      'xxx-n-s---',
      'o--ls---ss',
      '--s-xx-ll-',
      'xs-s----l-',
      'x--s---xl-',
      '-l-l-xlll-',
      '---ns-----',
    ],
    metadata: {
      level: 36,
      name: 'Legacy Level 34',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'es--l-----',
        'xxx-n-s---',
        'o--ls---ss',
        '--s-xx-ll-',
        'xs-s----l-',
        'x--s---xl-',
        '-l-l-xlll-',
        '---ns-----',
      ], 36),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 43
    }
  } as EnhancedPuzzle,
  {
    grid: ['-x-e-n-xxx', '-x--xlsllx', 's--n---x--', '-xs-xl-l--', '---------o'],
    metadata: {
      level: 37,
      name: 'Legacy Level 35',
      difficulty: 'medium',
      theme: getThemeForLevel(['-x-e-n-xxx', '-x--xlsllx', 's--n---x--', '-xs-xl-l--', '---------o'], 37),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 44
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'lllls--x--',
      'sxxx---x--',
      '-x--slell-',
      '-x--xlx-l-',
      's---n----s',
      '-xsxxx-ls-',
      '-xs-xlxx--',
      '-xs-x-o---',
      '--slll-n-x',
    ],
    metadata: {
      level: 38,
      name: 'Legacy Level 36',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'lllls--x--',
        'sxxx---x--',
        '-x--slell-',
        '-x--xlx-l-',
        's---n----s',
        '-xsxxx-ls-',
        '-xs-xlxx--',
        '-xs-x-o---',
        '--slll-n-x',
      ], 38),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 45
    }
  } as EnhancedPuzzle,
  {
    grid: [
      's-e-s-x-s-',
      'x--lx--lx-',
      'sss-ls--ss',
      'x-x-x-x-x-',
      '-sl-s---s-',
      '-x--l-o-ss',
      '-x--xlxxn-',
      's--sn-s--s',
      'x--sn-x--s',
    ],
    metadata: {
      level: 39,
      name: 'Legacy Level 37',
      difficulty: 'medium',
      theme: getThemeForLevel([
        's-e-s-x-s-',
        'x--lx--lx-',
        'sss-ls--ss',
        'x-x-x-x-x-',
        '-sl-s---s-',
        '-x--l-o-ss',
        '-x--xlxxn-',
        's--sn-s--s',
        'x--sn-x--s',
      ], 39),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 46
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'x-l-l-lxe-',
      '---n--xlx-',
      '-l--l--l--',
      '-lx-x--xx-',
      '-x-nxx--l-',
      '---nll----',
      'x--x--x--l',
      '-l-l---l-l',
      'o--xl---xl',
    ],
    metadata: {
      level: 40,
      name: 'Legacy Level 38',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'x-l-l-lxe-',
        '---n--xlx-',
        '-l--l--l--',
        '-lx-x--xx-',
        '-x-nxx--l-',
        '---nll----',
        'x--x--x--l',
        '-l-l---l-l',
        'o--xl---xl',
      ], 40),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 47
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'ss-ss-s--s',
      'o--s----s-',
      '-s--s--s--',
      '--ss-ns---',
      '-s--ss--s-',
      '---n---s-e',
      's--s--s--s',
      '---ns----s',
      's--s--s--s',
    ],
    metadata: {
      level: 41,
      name: 'Legacy Level 39',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'ss-ss-s--s',
        'o--s----s-',
        '-s--s--s--',
        '--ss-ns---',
        '-s--ss--s-',
        '---n---s-e',
        's--s--s--s',
        '---ns----s',
        's--s--s--s',
      ], 41),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 48
    }
  } as EnhancedPuzzle,
  {
    grid: [
      's-sss-s-ss',
      '---s--ss-s',
      '-----s-s--',
      's-ss--s---',
      '-s--s---s-',
      '-------s--',
      's--s--s--s',
      '-s--s-s--s',
      's--soe----',
    ],
    metadata: {
      level: 42,
      name: 'Legacy Level 40',
      difficulty: 'medium',
      theme: getThemeForLevel([
        's-sss-s-ss',
        '---s--ss-s',
        '-----s-s--',
        's-ss--s---',
        '-s--s---s-',
        '-------s--',
        's--s--s--s',
        '-s--s-s--s',
        's--soe----',
      ], 42),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 49
    }
  } as EnhancedPuzzle,
  {
    grid: [
      's-s---s--s',
      'o--s--ss-s',
      '-s---s-s--',
      's-ss--s---',
      '-s------s-',
      '--s-s--s--',
      's--s--s--s',
      '-s--s-s--s',
      's--s----e-',
    ],
    metadata: {
      level: 43,
      name: 'Legacy Level 41',
      difficulty: 'medium',
      theme: getThemeForLevel([
        's-s---s--s',
        'o--s--ss-s',
        '-s---s-s--',
        's-ss--s---',
        '-s------s-',
        '--s-s--s--',
        's--s--s--s',
        '-s--s-s--s',
        's--s----e-',
      ], 43),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 50
    }
  } as EnhancedPuzzle,
  {
    grid: [
      '-s--sll--s',
      '---sllss-s',
      '-s---s----',
      's-ss--s-ll',
      '-s-ll--s-l',
      '--s-s--s--',
      'sl-s--s--s',
      '-s--s-s--s',
      's--s-e-o--',
    ],
    metadata: {
      level: 44,
      name: 'Legacy Level 42',
      difficulty: 'medium',
      theme: getThemeForLevel([
        '-s--sll--s',
        '---sllss-s',
        '-s---s----',
        's-ss--s-ll',
        '-s-ll--s-l',
        '--s-s--s--',
        'sl-s--s--s',
        '-s--s-s--s',
        's--s-e-o--',
      ], 44),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 51
    }
  } as EnhancedPuzzle,
  {
    grid: [
      '-s-------s',
      'x---llss-s',
      'lsllls-xx-',
      'l-ss--s-ll',
      '-sxx-----l',
      '-xxll--s--',
      '---sl-s--s',
      '--x-l-s--s',
      '--ls-xl-l-',
      '-sl--ex--o',
    ],
    metadata: {
      level: 45,
      name: 'Legacy Level 43',
      difficulty: 'medium',
      theme: getThemeForLevel([
        '-s-------s',
        'x---llss-s',
        'lsllls-xx-',
        'l-ss--s-ll',
        '-sxx-----l',
        '-xxll--s--',
        '---sl-s--s',
        '--x-l-s--s',
        '--ls-xl-l-',
        '-sl--ex--o',
      ], 45),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 52
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'os-xx------s--',
      'x--sllssxx---x',
      'lsllls-xx-s-xx',
      'l-ss--s-ll-xx-',
      '-s-l--xx---lxx',
      '-x-ll--s--xx-l',
      '-x---sl-s--ll-',
      '---xx-l-s--s-e',
    ],
    metadata: {
      level: 46,
      name: 'Legacy Level 44',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'os-xx------s--',
        'x--sllssxx---x',
        'lsllls-xx-s-xx',
        'l-ss--s-ll-xx-',
        '-s-l--xx---lxx',
        '-x-ll--s--xx-l',
        '-x---sl-s--ll-',
        '---xx-l-s--s-e',
      ], 46),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 53
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'o---e-------------',
      'xxxx-xxxxxxxxxxxx-',
      '----------------x-',
      '-xxx-xxxxxxxxxx-x-',
      '--------n---------',
      'xxxx------------xx',
    ],
    metadata: {
      level: 47,
      name: 'Legacy Level 45',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'o---e-------------',
        'xxxx-xxxxxxxxxxxx-',
        '----------------x-',
        '-xxx-xxxxxxxxxx-x-',
        '--------n---------',
        'xxxx------------xx',
      ], 47),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 54
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xxxx-xxxxxxxxxxxxx',
      'e-----------------',
      'xxxx-xxxxxxxx-xxx-',
      'o-----------------',
      'xxxx-xxxxxxxx-xxx-',
      '----------------x-',
      '-xxx-xxxxxxxx-x-x-',
      '---------n--------',
      'xxxx------------xx',
      'xxxx-xxxxxxxx-xxxx',
      'xxxx----------xxxx',
    ],
    metadata: {
      level: 48,
      name: 'Legacy Level 46',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xxxx-xxxxxxxxxxxxx',
        'e-----------------',
        'xxxx-xxxxxxxx-xxx-',
        'o-----------------',
        'xxxx-xxxxxxxx-xxx-',
        '----------------x-',
        '-xxx-xxxxxxxx-x-x-',
        '---------n--------',
        'xxxx------------xx',
        'xxxx-xxxxxxxx-xxxx',
      ], 48),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 55
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xxxx----------xxxx',
      'xxxx-xxxx-xxx-xxxx',
      '------------------',
      'xxxx-xxxx-xxx-xxx-',
      'o-----------------',
      'xxxx-xxxx-xxx-x-x-',
      '----------xxx-x-x-',
      '-xxx-xxxx-xxx-x-x-',
      '---------n--------',
      'xxxx------------xx',
      'xxxx-xxxx-xxx-xxxx',
      'e-------------xxxx',
      'xxxx------xxxxxxxx',
    ],
    metadata: {
      level: 49,
      name: 'Legacy Level 47',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xxxx----------xxxx',
        'xxxx-xxxx-xxx-xxxx',
        '------------------',
        'xxxx-xxxx-xxx-xxx-',
        'o-----------------',
        'xxxx-xxxx-xxx-x-x-',
        '----------xxx-x-x-',
        '-xxx-xxxx-xxx-x-x-',
        '---------n--------',
        'xxxx------------xx',
        'xxxx-xxxx-xxx-xxxx',
        'e-------------xxxx',
        'xxxx------xxxxxxxx',
      ], 49),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 56
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xxxx----xx----xxxx',
      'xxox-xx-xx-xx-xxxx',
      'e----------xx-----',
      'xx-x-xxxxx-xx-xxx-',
      '--------n---------',
      '-x-x-xx-xx-xx-x-x-',
      '-----------xx-x-x-',
      '-x-x-xx-xx-xx-x-x-',
      '-----n------------',
      'xx--------------x-',
      'xxxx-xxxxx-xx-xxx-',
      '--------------xxx-',
      'xxxx--------------',
    ],
    metadata: {
      level: 50,
      name: 'Legacy Level 48',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xxxx----xx----xxxx',
        'xxox-xx-xx-xx-xxxx',
        'e----------xx-----',
        'xx-x-xxxxx-xx-xxx-',
        '--------n---------',
        '-x-x-xx-xx-xx-x-x-',
        '-----------xx-x-x-',
        '-x-x-xx-xx-xx-x-x-',
        '-----n------------',
        'xx--------------x-',
        'xxxx-xxxxx-xx-xxx-',
        '--------------xxx-',
        'xxxx--------------',
      ], 50),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 57
    }
  } as EnhancedPuzzle,
  {
    grid: [
      'xxxx----xx----xxxx',
      'xxox-xx-xx-xx-xxxx',
      'e----------xx-----',
      '-x-x-xx-xx-xx-x-x-',
      '-----n-------s----',
      'xx--------------x-',
      'xxxx-xxxxx-xx-xxx-',
      '--------------xxx-',
      'xxxx----n---------',
    ],
    metadata: {
      level: 51,
      name: 'Legacy Level 49',
      difficulty: 'medium',
      theme: getThemeForLevel([
        'xxxx----xx----xxxx',
        'xxox-xx-xx-xx-xxxx',
        'e----------xx-----',
        '-x-x-xx-xx-xx-x-x-',
        '-----n-------s----',
        'xx--------------x-',
        'xxxx-xxxxx-xx-xxx-',
        '--------------xxx-',
        'xxxx----n---------',
      ], 51),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 58
    }
  } as EnhancedPuzzle,
  {
    grid: ['oe'],
    metadata: {
      level: 52,
      name: 'Legacy Level 50',
      difficulty: 'medium',
      theme: getThemeForLevel(['oe'], 52),
      collectibles: [],
      minStars: 0,
      timeTarget: 60,
      stepTarget: 59
    }
  } as EnhancedPuzzle,
];
